// src/lib/pushChannel.ts
// Клиент пуш-шлюза реального времени — см. docs/tasks/2026-07-30-push-gateway.md, §3.
//
// Цикл: GET /api/push/token/ -> либо {enabled:false} (шлюз выключен/недоступен пользователю —
// статус 'disabled', наружу ничего не летит, работает старый Django-SSE + поллинг как раньше),
// либо {enabled:true,url,token} -> EventSource на шлюз. Сигнальная модель (§0 плана): шлюз
// НЕ отдаёт данные, только {scope, version} — потребитель сам решает, что перечитать штатным API.
//
// Реконнект — СВОЙ, не нативный EventSource retry: протухший токен (не-200) убивает
// EventSource навсегда (канон спеки SSE), плюс токен по дизайну одноразовый на подключение
// (ротация шлюза 240с > TTL токена 120с) — поэтому каждый реконнект идёт за НОВЫМ токеном.
// Лестница задержек 1/2/5/10/30с; после 5 подряд неудач — статус 'failed' и остановка
// автоматических попыток до фокуса окна (тот же паттерн, что был у legacy SSE в App.tsx).
import { apiJson } from './apiClient';
import { BACKEND } from '../config';
import { csrfFetch } from '../utils/csrfFetch';

export type PushSignal = { scope: string; version: string };
export type PushStatus = 'disabled' | 'connecting' | 'live' | 'failed';

type PushTokenResponse = {
  enabled: boolean;
  url?: string;
  token?: string;
  expires_in?: number;
};

const RECONNECT_DELAYS_MS = [1000, 2000, 5000, 10000, 30000];
const MAX_CONSECUTIVE_FAILURES = 5;

let currentStatus: PushStatus = 'disabled';

const setStatus = (status: PushStatus): void => {
  if (currentStatus === status) return;
  currentStatus = status;
  window.dispatchEvent(
    new CustomEvent<{ status: PushStatus }>('crm:push-status', { detail: { status } })
  );
};

export const getPushStatus = (): PushStatus => currentStatus;

// Тест-хелпер: статус — модульный синглтон (один канал на вкладку), между тестами его
// нужно сбрасывать явно (тот же паттерн, что __resetSoftSyncDedupCacheForTests в softSync.ts).
export const __resetPushChannelStatusForTests = (): void => {
  currentStatus = 'disabled';
};

// Хелпер для потребителей 'crm:push-status' (GlobalDataSyncListener/NotificationsListener/
// useTasksSync): деривация pushLive с grace через транзитный 'connecting'. Штатная ротация
// шлюза (PUSH_STREAM_ROTATE_SECONDS=240с) закрывает EventSource каждые ~4 мин, наш onerror
// шлёт статус 'connecting' на 1-2с, пока не подключимся новым токеном — если бы pushLive
// дословно повторял 'status === live', на КАЖДУЮ такую ротацию легаси-SSE/поллеры (App.tsx,
// useTasksSync.ts) кратко переоткрывались бы и тут же закрывались снова, подтачивая цель
// «0 стримов на gunicorn» до этапа D выпила (раунд правок №1, 2026-07-30). pushLive гаснет
// ТОЛЬКО на 'failed'/'disabled' — на 'connecting' остаётся как было (реконнект внутри
// ротации не должен быть виден потребителям).
export const nextPushLive = (prevLive: boolean, status: PushStatus): boolean => {
  if (status === 'live') return true;
  if (status === 'failed' || status === 'disabled') return false;
  return prevLive;
};

// startPushChannel — единственная точка входа. Вызывается один раз (GlobalDataSyncListener
// в App.tsx) при isAuthenticated && role !== 'error_analyst'; возвращает функцию остановки.
export const startPushChannel = (): (() => void) => {
  let stopped = false;
  let source: EventSource | null = null;
  let reconnectTimer: number | null = null;
  let failureCount = 0;
  let attempt = 0;
  let focusHandler: (() => void) | null = null;

  const clearReconnectTimer = () => {
    if (reconnectTimer != null) {
      window.clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
  };

  const closeSource = () => {
    if (source) {
      source.close();
      source = null;
    }
  };

  const detachFocusRetry = () => {
    if (focusHandler) {
      window.removeEventListener('focus', focusHandler);
      focusHandler = null;
    }
  };

  const scheduleReconnect = () => {
    const delay = RECONNECT_DELAYS_MS[Math.min(attempt, RECONNECT_DELAYS_MS.length - 1)];
    attempt += 1;
    clearReconnectTimer();
    reconnectTimer = window.setTimeout(() => {
      reconnectTimer = null;
      void cycle();
    }, delay);
  };

  const attachFocusRetry = () => {
    if (focusHandler) return;
    const onFocus = () => {
      void csrfFetch(`${BACKEND}/api/check-session/`)
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => {
          if (stopped) return;
          if (d?.authenticated) {
            detachFocusRetry();
            failureCount = 0;
            attempt = 0;
            void cycle();
          }
        })
        .catch(() => {
          // сеть/сессия ещё не готова — попробуем на следующем фокусе
        });
    };
    focusHandler = onFocus;
    window.addEventListener('focus', onFocus);
  };

  const handleFailure = () => {
    closeSource();
    if (stopped) return;
    failureCount += 1;
    if (failureCount >= MAX_CONSECUTIVE_FAILURES) {
      setStatus('failed');
      window.dispatchEvent(new Event('crm:push-failed'));
      attachFocusRetry();
      return;
    }
    scheduleReconnect();
  };

  const connect = (url: string, token: string) => {
    const es = new EventSource(`${url}/events?token=${encodeURIComponent(token)}`);
    source = es;

    // Намеренно слушаем 'hello' от шлюза, а НЕ es.onopen (отклонение от буквы §3 плана,
    // зафиксировано в раунде правок №1, 2026-07-30): onopen срабатывает на голом открытии
    // TCP/HTTP-соединения, до того как токен вообще проверен шлюзом; 'hello' шлётся шлюзом
    // ПОСЛЕ успешной проверки токена (§1 плана, первым событием потока) — по сути строже и
    // корректнее, чем «open» в исходном тексте плана: open ещё не значит «токен принят».
    const onHello = () => {
      failureCount = 0;
      attempt = 0;
      setStatus('live');
      window.dispatchEvent(new Event('crm:push-open'));
    };

    const onSignal = (event: Event) => {
      const msg = event as MessageEvent<string>;
      try {
        const parsed = JSON.parse(msg.data || '{}') as PushSignal;
        window.dispatchEvent(new CustomEvent<PushSignal>('crm:push-signal', { detail: parsed }));
      } catch {
        // битый payload — сигнальная модель это состояние, не журнал; ждём следующий сигнал
      }
    };

    es.addEventListener('hello', onHello);
    es.addEventListener('signal', onSignal);
    es.onerror = () => {
      es.removeEventListener('hello', onHello);
      es.removeEventListener('signal', onSignal);
      handleFailure();
    };
  };

  const cycle = async (): Promise<void> => {
    if (stopped) return;
    setStatus('connecting');

    let data: PushTokenResponse;
    try {
      data = await apiJson<PushTokenResponse>(`${BACKEND}/api/push/token/`);
    } catch {
      handleFailure();
      return;
    }
    if (stopped) return;

    if (!data.enabled || !data.url || !data.token) {
      setStatus('disabled');
      return;
    }

    connect(data.url, data.token);
  };

  void cycle();

  return () => {
    stopped = true;
    clearReconnectTimer();
    closeSource();
    detachFocusRetry();
    setStatus('disabled');
  };
};
